// config/apiEndpoints.js - Centralized API endpoints configuration

export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: "/api/UserLogin/User_Login",
    LOGOUT: "/api/UserLogin/User_Logout",
    VALIDATE_SESSION: "/api/UserLogin/ValidateSession",
    REFRESH_TOKEN: "/api/UserLogin/RefreshToken",
    CHECK_EMAIL_ID: "/api/UserLogin/CheckEmailId",
    VALIDATE_OTP: "/api/UserLogin/ValidateOTP",
    SET_NEW_PASSWORD: "/api/UserLogin/SetNewPassword",
  },

  // User management endpoints
  USER: {
    PROFILE: "/api/User/Profile",
    UPDATE_PROFILE: "/api/User/UpdateProfile",
    CHANGE_PASSWORD: "/api/UserLogin/ChangePwd",
    CHANGE_ACCESS_CODE: "/api/UserLogin/ChangeAccessCode",
    GET_PERMISSIONS: "/api/User/GetPermissions",
    GET_USERLIST: "/api/eDelegation/UsersActivity",
    GET_USER_PROFILE_COMMONDATA: "/api/eDelegation/GetPageRecordsByPageName",
    GET_INDIVIDUAL_USER: "/api/eDelegation/GetIndividualData",
    GET_EXTERNAL_ENTITY_ACCESS: "/api/eDelegation/GetExternalEntityAccess",
    SAVE_UPDATE_USER: "/api/eDelegation/StaffRegistrationNew",
    ACTIVATE_DEACTIVATE_USER: "/api/eDelegation/UsersActivity",
    SAVE_ESIGNATURE: "/api/eDelegation/UsersActivity", // Separate API to save e-signature (base64) to DB//UpdateSignature
  },

  ROLES: {
    StaffRolesActivity: "/api/Settings/StaffRolesActivity",
  },

  /** Legacy DMS — QMS uses /api/QMS/*; eISF uses /api/eISF/* document masters */
  DMS: {
    DOCUMENT_TYPE_ACTIVITY: "/api/DMS/DocumentTypeActivity",
  },

  /**
   * QMS (clone of DMS API — ClinicalTrialAPI QMSController → DMSSetUp).
   * React UI should use QMS.* endpoints only.
   */
  QMS: {
    DOCUMENT_TYPE_ACTIVITY: "/api/QMS/DocumentTypeActivity",
    DOCUMENT_CATEGORY_ACTIVITY: "/api/QMS/DocumentCategoryActivity",
    MY_VAULT_ACTIVITY: "/api/QMS/DMS_MyVaultActivity",
    MY_VAULT_UPLOAD: "/api/QMS/DMS_MyVaultUploadDocument",
    SHARED_WITH_ME_ACTIVITY: "/api/QMS/DMS_SharedWithMeActivity",
    DOCUMENT_COMMENT_ACTIVITY: "/api/QMS/DMS_DocumentCommentActivity",
    CHECK_IN_CHECK_OUT_ACTIVITY: "/api/QMS/DMS_DocumentCheckInCheckOutActivity",
    DOCUMENT_QUIZZ_ACTIVITY: "/api/QMS/DMS_DocumentQuizzActivity",
    MY_LEARNING_ACTIVITY: "/api/QMS/DMS_MyLearningActivity",
    DASHBOARD_ACTIVITY: "/api/QMS/DashboardActivity",
    CHANGE_REQUEST_ACTIVITY: "/api/QMS/DocumentChangeRequestActivity",
    TRACKER_ACTIVITY: "/api/QMS/DMSTrackerActivity",
    CERTIFICATE_TEMPLATE_ACTIVITY: "/api/QMS/DMSCertificatetemplateActivity",
    DOCUMENT_LIBRARY_V2_ACTIVITY: "/api/QMS/DocumentLibraryV2Activity",
    MY_LEARNING_V2_ACTIVITY: "/api/QMS/MyLearningV2Activity",
    QUESTION_BANK_V2_ACTIVITY: "/api/QMS/QuestionBankV2Activity",
    QUIZ_V2_ACTIVITY: "/api/QMS/QuizV2Activity",
    CONFIG_GET: "/api/QMS/GetConfig",
    CONFIG_SAVE: "/api/QMS/SaveConfig",
  },

  // Site management (Tbl_OrganizationSite / Usp_OrganizationSiteActivity_NEWUI)
  SITE: {
    SITE_ACTIVITY: "/api/Site/SiteActivity",
  },

  /** Physical site / building / floor / room capacity (Admin) */
  CAPACITY_MANAGEMENT: {
    ACTIVITY: "/api/CapacityManagement/CapacityManagementActivity",
  },

  ORG_SECURITY: {
    GET_LOGIN_IPS: "/api/Settings/GetOrganizationLoginIps",
    LOGIN_IP_ACTIVITY: "/api/Settings/OrganizationLoginIpActivity",
    GET_STAFF_LOGIN_IPS: "/api/Settings/GetStaffLoginIps",
    STAFF_LOGIN_IP_ACTIVITY: "/api/Settings/StaffLoginIpActivity",
    GET_SETUP_CLIENT_IP: "/api/Settings/GetSetupClientIp",
  },

  CREDENTIALS: {
    GET_ORG_POLICY: "/api/Settings/GetOrganizationCredentialPolicy",
    ORG_POLICY_ACTIVITY: "/api/Settings/OrganizationCredentialPolicyActivity",
    GET_STAFF_STATUS: "/api/UserLogin/GetStaffCredentialStatus",
    ACCEPT_TERMS: "/api/UserLogin/SaveTermsAndConditionsFlag",
    CHANGE_PASSWORD: "/api/UserLogin/ChangePwd",
    CHANGE_ACCESS_CODE: "/api/UserLogin/ChangeAccessCode",
  },

  REMINDERS: {
    GET_ORG_SETTINGS: "/api/Settings/GetOrganizationReminderSettings",
    ORG_SETTINGS_ACTIVITY: "/api/Settings/OrganizationReminderSettingsActivity",
  },

  ORGANIZATION: {
    SETUP_ACTIVITY: "/api/Settings/OrganizationSetUpActivity",
    LOCATION_ACTIVITY: "/api/Settings/LocationSetUpActivity",
    STAFF_DOCUMENT: "/api/Settings/SaveStaffDocument",
    CHANGE_ORGANIZATION: "/api/Settings/ChangeOrganization",
    /** AQ Super Admin — list all customer organisations */
    GET_ORGANIZATIONS_DETAILS: "/api/Settings/GetOrganizationsDetails",
    SUPER_ADMIN_QMS_TRAINING_FLAG: "/api/Settings/SuperAdminOrgQmsTrainingFlag",
    SUPER_ADMIN_ORG_MODULES: "/api/Settings/SuperAdminOrgModules",
    SUPER_ADMIN_SPONSOR_CRO_MANAGEMENT_FLAG:
      "/api/Settings/SuperAdminOrgSponsorCroManagementFlag",
    SUPER_ADMIN_SPONSOR_CRO_STUDY_ACCESS_EMAIL_TEMPLATE:
      "/api/Settings/SuperAdminSponsorCroStudyAccessEmailTemplate",
    SUPER_ADMIN_MFA_FLAG: "/api/Settings/SuperAdminOrgMfaFlag",
    SUPER_ADMIN_MULTIPLE_REMINDERS_FLAG: "/api/Settings/SuperAdminOrgMultipleRemindersFlag",
    SUPER_ADMIN_FAQ_AI_FLAG: "/api/Faq/SuperAdminOrgFaqAiFlag",
    SUPER_ADMIN_FAQ_AI_PLATFORM_SETTINGS: "/api/Faq/SuperAdminFaqAiPlatformSettings",
    GET_FAQ_AI_USAGE: "/api/Faq/GetFaqAiUsage",
    ORG_MESSAGING_SETTINGS: "/api/Settings/OrgMessagingSettings",
    GET_TWILIO_OUTBOUND_MESSAGES: "/api/Settings/GetTwilioOutboundMessages",
    CREATE_ORGANIZATION: "/api/Settings/CreateOrganization",
  },

  SETTINGS: {
    /** Org-scoped Department Manager (Admin > Departments) */
    DEPARTMENT_MANAGER: "/api/Settings/DepartmentManager",
    /** Send Bulk Email/SMS — filter recipients + send (legacy SettingController) */
    STUDY_WISE_VOLUNTEERS: "/api/Settings/StudyWiseVolunteers",
    SEND_EMAIL_SMS: "/api/Settings/SendEmailSMS",
    /** Payment notification email template (Participant Manager — System Administrator) */
    PAYMENT_TEMPLATE_ACTIVITY: "/api/Settings/PaymentTemplateActivity",
  },

  /** Platform FAQ + FAQ AI — FaqController (not Settings) */
  FAQ: {
    GET_PLATFORM_FAQS: "/api/Faq/GetPlatformFaqs",
    SAVE_PLATFORM_FAQ: "/api/Faq/SavePlatformFaq",
    DELETE_PLATFORM_FAQ: "/api/Faq/DeletePlatformFaq",
    DELETE_ALL_PLATFORM_FAQS: "/api/Faq/DeleteAllPlatformFaqs",
    IMPORT_PLATFORM_FAQS: "/api/Faq/ImportPlatformFaqs",
    GET_USER_GUIDES: "/api/Faq/GetPlatformUserGuides",
    SAVE_USER_GUIDE: "/api/Faq/SavePlatformUserGuide",
    DELETE_USER_GUIDE: "/api/Faq/DeletePlatformUserGuide",
    GET_USER_GUIDE_FILE: "/api/Faq/GetPlatformUserGuideFile",
    REORDER_USER_GUIDES: "/api/Faq/ReorderPlatformUserGuides",
    GET_FAQ_AI_STATUS: "/api/Faq/GetFaqAiStatus",
    ASK_PLATFORM_FAQ: "/api/Faq/AskPlatformFaq",
    ASK_FORM_BUILDER_AI: "/api/Faq/AskFormBuilderAi",
    GET_FAQ_AI_CHAT_HISTORY: "/api/Faq/GetFaqAiChatHistory",
    GET_FAQ_AI_CHAT_SESSION: "/api/Faq/GetFaqAiChatSession",
    SET_FAQ_AI_FEEDBACK: "/api/Faq/SetFaqAiFeedback",
  },


  /** Lookup / prepopulated data for dropdowns (Usp_LookupData_NEWUI) */
  LOOKUP: {
    LOOKUP_ACTIVITY: "/api/Lookup/LookupActivity",
  },

  // Study management endpoints
  STUDY: {
    GET_ALL_STUDIES: "/api/eSchedule/ScheduleStudyDetails",
    GET_STUDY_MGMT_LIST: "/api/eSchedule/GetStudyMgmtList",
    GET_BY_ID: "/api/Study/GetById",
    CREATE: "/api/Study/Create",
    UPDATE: "/api/Study/Update",
    DELETE: "/api/Study/Delete",
    /** Add/Update study (legacy form parity with Add Study modal) */
    STUDY_ACTIVITY: "/api/Settings/StudyActivity",
  },

  /** eSchedule cohort wizard + schedule setup (ClinicalTrialAPI eScheduleCohortController) */
  ESCHEDULE: {
    GET_COHORT_SETUP: "/api/eScheduleCohort/GetStudyScheduleSetUpCohort",
    SINGLE_GROUP_SETUP: "/api/eScheduleCohort/CohortSingleGroupScheduleSetUp",
    MULTI_GROUP_STEP1: "/api/eScheduleCohort/MultipleGroupStep1",
    MULTI_GROUP_RUNIN: "/api/eScheduleCohort/MultipleGroupStep1RunIn",
    MULTI_GROUP_STEP2: "/api/eScheduleCohort/MultipleGroupStep2",
    MULTI_GROUP_STEP3: "/api/eScheduleCohort/MultipleGroupStep3ApplyVisit",
    GET_GROUP_INFO: "/api/eScheduleCohort/GetGroupInfo",
    GET_AUDIT_LOG: "/api/eScheduleCohort/GetScheduleAuditLog",
    WRITE_AUDIT_LOG: "/api/eScheduleCohort/WriteScheduleAuditLog",
    SCHEDULE_LIVE: "/api/eScheduleCohort/CohortScheduleLive",
    SAVE_UPDATE_DELETE_GROUP: "/api/eScheduleCohort/SaveUpdateDeleteGroup",
    CLONE_SCHEDULE_TO_STUDY: "/api/eScheduleCohort/CloneCohortScheduleToStudy",
    /** Per-visit detail save (legacy ESchedule parity) */
    INDIVIDUAL_VISIT: "/api/eSchedule/IndividualVisitDetails",
    CHANGE_VISIT_NAME: "/api/eSchedule/ChangeVisitName",
    SWAP_VISIT_ORDER: "/api/eSchedule/SwapVisitOrder",
    SCREENING_DETAILS: "/api/eSchedule/ScreeningDetails",
    VISITS_PER_STUDY: "/api/eSchedule/VisitsPerStudyDetails",
    SAVE_ESCHEDULE: "/api/eSchedule/SaveESchedule",
    /** Planner / bulk comm study dropdown (GetPlannerstudyListdata) */
    GET_STUDY_SCHEDULE_DATA: "/api/eSchedule/GetStudyScheduledata",
    /** Settings APIs used per visit (legacy parity) */
    SAVE_ECOMM_SETUP: "/api/Settings/SaveECommSetup",
    DEPARTMENT_ROLE: "/api/Settings/DepartmentRoleAssignAcivity",
    STUDY_WORKSHEET: "/api/Settings/StudyWorkSheetActivity",
  },

  /** ePlanner — appointments, calendar, occupancy (ClinicalTrialAPI ePlannerController) */
  EPLANNER: {
    SET_VISIT_PLANNER: "/api/ePlanner/SetVisitPlanner",
    GET_APPOINTMENT: "/api/ePlanner/GetAppointment",
    GET_APPOINTMENT_FAST: "/api/ePlanner/GetAppointmentFast",
    GET_APPOINTMENTS_DATA: "/api/ePlanner/GetAppointmentsdata",
    APPOINTMENT_ACTIVITY: "/api/ePlanner/AppointmentActivity",
    APPOINTMENT_ACTIVITY_NEW: "/api/ePlanner/AppointmentActivityNew",
    GET_CALENDAR_VIEW: "/api/ePlanner/GetCalendarViewAPI",
    CALENDAR_LABEL_ACTIVITY: "/api/ePlanner/CalendarLabelActivity",
    GET_INPUT_BASED_VOL_DETAILS: "/api/ePlanner/GetInputBasedVolDetails",
    VIDEO_CONSULTATION: "/api/ePlanner/VideoConsultation",
    VIDEO_CONSULTATION_INVITATION: "/api/ePlanner/VideoConsultationInvitation",
    INVITE: "/api/ePlanner/Invite",
    SEARCH_VOLUNTEER: "/api/ePlanner/SearchVolunteer",
    APPOINTMENT_LOCATION_ACTIVITY: "/api/ePlanner/AppointmentLocationActivity",
    GET_STUDY_LOCATION_APPOINTMENTS: "/api/ePlanner/GetStudyLocationAppointments",
    GET_LOCATION_OCCUPANCY_DATA: "/api/ePlanner/GetLocationOccupancyData",
    GET_OCCUPANCY_BY_ROOM: "/api/ePlanner/GetOccupancyDetailsByRoomId",
    GET_EXCEL_DATA_OF_LOCATION: "/api/ePlanner/GetExcelDataOfLocation",
    GET_UNOCCUPIED_CAPACITY_DATA: "/api/ePlanner/GetUnOccupiedCapacityData",
    /** Analytics reports (same SPs as legacy Settings analytics) */
    GET_ANALYTICS: "/api/ePlanner/GetAnalytics",
    STUDY_WISE_APPOINTMENT: "/api/ePlanner/StudyWiseAppointment",
    GET_OW_APP_ANALYTICS: "/api/ePlanner/GetOWAppAnalytics",
    LOCATION_OCCUPANCY_ANALYTICS: "/api/ePlanner/LocationOccupancyAnalytics",
    /** WhatsApp two-way Communication inbox */
    LIST_WHATSAPP_THREADS: "/api/ePlanner/ListWhatsAppThreads",
    GET_WHATSAPP_THREAD: "/api/ePlanner/GetWhatsAppThread",
    REPLY_WHATSAPP: "/api/ePlanner/ReplyWhatsApp",
    /** Study / location / group / visit filters for planner screens */
    PLANNER_STUDY_BUNDLE: "/api/eSchedule/ScheduleStudyDetails",
  },

  /** eRecruitment — Participant Manager + E-Planner volunteer lookup */
  ERECRUITMENT: {
    SAVE_VOLUNTEER: "/api/eRecruitment/SaveVolunteer",
    SAVE_VOLUNTEER_DOCUMENT: "/api/eRecruitment/SaveVolunteerDocument",
    REPAIR_VOLUNTEER_DOCUMENTS: "/api/eRecruitment/RepairVolunteerDocuments",
    SEND_SMS_EMAIL_TO_VOLUNTEER: "/api/eRecruitment/SendSMSEmailToVolunteer",
    SEND_SMS_EMAIL_TO_TEST_USER: "/api/eRecruitment/SendSMSEmailToTestUser",
    VOLUNTEER_ACTIVITY: "/api/eRecruitment/VolunteerActivity",
    ETHNICITY: "/api/eRecruitment/Ethnicity",
    CLINICAL_DETAILS_PER_VOL: "/api/eRecruitment/ClinicalDetailsPerVol",
    SAVE_MEDICAL_HISTORY: "/api/eRecruitment/SaveMedicalHistory",
    GET_MEDICAL_HISTORY: "/api/eRecruitment/GetMedicalHistory",
    SAVE_SURGICAL_HISTORY: "/api/eRecruitment/SaveSurgicalHistory",
    GET_SURGICAL_HISTORY: "/api/eRecruitment/GetSurgicalHistory",
    SEARCH_SNOMED: "/api/eRecruitment/SearchSnomed",
    SNOMED_FILTER: "/api/eRecruitment/SnomedFilter",
    SAVE_FAMILY_HISTORY: "/api/eRecruitment/SaveFamilyHistory",
    GET_FAMILY_HISTORY: "/api/eRecruitment/GetFamilyHistory",
    SAVE_ALLERGY_HISTORY: "/api/eRecruitment/SaveAllergyHistory",
    GET_ALLERGY_HISTORY: "/api/eRecruitment/GetAllergyHistory",
    SAVE_PHYSICAL_MEASUREMENT: "/api/eRecruitment/SavePhysicalMeasurementHistory",
    GET_PHYSICAL_MEASUREMENT: "/api/eRecruitment/GetPhysicalMeasurementHistory",
    MEDICATION_HISTORY: "/api/eRecruitment/MedicationHistory",
    GET_MEDICATION_HISTORY: "/api/eRecruitment/GetMedicationHistory",
    TEMPLATE_SETUP: "/api/eRecruitment/TemplateSetupAPI",
    PARTICIPANTS_INFO: "/api/eRecruitment/ParticipantsinfoAPI",
    VOLUNTEER_BY_PARTICIPANT: "/api/eRecruitment/VolunteerByParticipant",
    TEMPLATE_FOR_COMMUNICATION: "/api/eRecruitment/TemplateForCommunication",
    SAVE_APPOINTMENT_PAYMENT: "/api/eRecruitment/SaveAppointmentPayment",
    SEND_PAYMENT_EMAIL: "/api/eRecruitment/SendPaymentEmail",
    SEND_CODE: "/api/eRecruitment/SendCode",
    GET_VOLUNTEER_AUDIT_LOG: "/api/eRecruitment/GetVolunteerAuditLog",
  },

  /** UserLogin endpoints used by video consultation feedback */
  USER_LOGIN: {
    VIRTUAL_CONSULTATION: "/api/UserLogin/VirtualConsultation",
    SAVE_FEEDBACK: "/api/UserLogin/SaveFeedback",
  },

  // ISF endpoints
  ISF: {
    GET_ALL: "/api/ISF/GetAll",
    GET_BY_ID: "/api/ISF/GetById",
    CREATE: "/api/ISF/Create",
    UPDATE: "/api/ISF/Update",
    DELETE: "/api/ISF/Delete",
    TEMPLATE_LIST: "/api/eISF/GetTemplateListData",
    GET_TEMPLATE_BY_ID: "/api/eISF/SaveTemplates",
    GETSTUDYLIST: "/api/eSchedule/ScheduleStudyDetails",
    GET_STUDY_FOLDERS: "/api/eISF/GetStudyFolderDocuments",
    SAVE_FOLDER_DOCUMENTS: "/api/eISF/SaveFolderDocuments",
    FILE_DELETE_LOCK: "/api/eISF/DeleteFolder",
    FILE_COMMENTS: "/api/eISF/FileComment",
    COMMENT_MENTION_CANDIDATES: "/api/eISF/CommentMentionCandidates",
    MY_TAGGED_COMMENTS: "/api/eISF/MyTaggedComments",
    FILE_FOLDER_MOVE: "/api/eISf/MoveDocumentToFolder",
    FILE_SAVE_SIGN: "/api/eISf/SaveFileSignature",
    FILE_FOLDER_RESTORE: "/api/eISF/RestoreFolder",
    FOLDER_DELETE: "/api/eISF/DeleteFolder",
    GET_DASHBOARD_DATA: "/api/eISF/GetDashboardData",
    //GET_AUDIT_LOGS: "/api/eISF/GetAuditLogs",
    GET_ISF_EVENT_LOG: "/api/eISF/GetEventLogDetails",
    GET_DOWNLOAD_AUDIT_LOGS: "/api/eISF/GetEventLogDetails",
    REQUEST_DOWNLOAD_AUDIT_LOG: "/api/eISF/GetEventLogDetails",
    GENERATE_CERTIFICATION: "/api/eISF/GenerateCertification",
    PLACEHOLDER_ACTIVITY: "/api/eISF/PlaceholderActivity",
    MILESTONE_ACTIVITY: "/api/eISF/MilestoneActivity",
    STUDY_SITE_CONFIG: "/api/eISF/StudySiteConfig",
    STUDY_REFERENCE_DATE: "/api/eISF/StudyReferenceDate",
    STUDY_MILESTONE: "/api/eISF/StudyMilestone",
    STUDY_GATE: "/api/eISF/StudyGate",
    EISF_DEFAULT_FOLDER: "/api/eISF/EISFDefaultFolder",
    DOCUMENT_TYPE_ACTIVITY: "/api/eISF/DocumentTypeActivity",
    DOCUMENT_CATEGORY_ACTIVITY: "/api/eISF/DocumentCategoryActivity",
    CONFIG_GET: "/api/eISF/GetConfig",
    CONFIG_SAVE: "/api/eISF/SaveConfig",
  },

  // File management endpoints
  FILES: {
    UPLOAD: "/api/Files/Upload",
    DOWNLOAD: "/api/Files/Download",
    DELETE: "/api/Files/Delete",
  },

  /**
   * CAPA (corrective / preventive action).
   * Uncomment usages in capaService.js when backend endpoints are live.
   */
  CAPA: {
    SAVE: "/api/CAPA/Save",
    UPDATE: "/api/CAPA/Update",
    REMOVE: "/api/CAPA/Remove",
    TRANSITION_STATUS: "/api/CAPA/TransitionStatus",
    LIST: "/api/CAPA/GetList",
    GET_BY_ID: "/api/CAPA/GetById",
    DASHBOARD: "/api/CAPA/GetDashboard",
    ACTION_ITEMS_LIST: "/api/CAPA/GetActionItems",
    SAVE_ACTION_ITEM: "/api/CAPA/SaveActionItem",
    UPDATE_ACTION_ITEM: "/api/CAPA/UpdateActionItem",
    DELETE_ACTION_ITEM: "/api/CAPA/DeleteActionItem",
    GET_APPROVER_RECORDS: "/api/CAPA/GetApproverRecordsByCapa",
    INSERT_APPROVER_RECORD: "/api/CAPA/InsertApproverRecord",
    UPDATE_APPROVER_RECORD: "/api/CAPA/UpdateApproverRecordStatus",
    DELETE_APPROVER_RECORD: "/api/CAPA/DeleteApproverRecord",
    TASKS_LIST: "/api/CAPA/GetTasks",
    APPROVALS_QUEUE: "/api/CAPA/GetApprovalsQueue",
    EFFECTIVENESS_QUEUE: "/api/CAPA/GetEffectivenessQueue",
    AUDIT_LOG: "/api/CAPA/GetAuditLog",
    CONFIG_GET: "/api/CAPA/GetConfig",
    CONFIG_SAVE: "/api/CAPA/SaveConfig",
    IMPLEMENTATION_TASKS_LIST: "/api/CAPA/GetImplementationTasksByCapa",
    SAVE_IMPLEMENTATION_TASK: "/api/CAPA/SaveImplementationTask",
    UPDATE_IMPLEMENTATION_TASK: "/api/CAPA/UpdateImplementationTaskStatus",
    DELETE_IMPLEMENTATION_TASK: "/api/CAPA/DeleteImplementationTask",
    VERIFICATION_REQUESTS_LIST: "/api/CAPA/GetVerificationRequestsByCapa",
    SAVE_VERIFICATION_REQUEST: "/api/CAPA/SaveVerificationRequest",
    SUBMIT_VERIFICATION_REQUEST: "/api/CAPA/SubmitVerificationRequest",
    DELETE_VERIFICATION_REQUEST: "/api/CAPA/DeleteVerificationRequest",
    REPORTS_EXPORT: "/api/CAPA/ExportReport",
  },

  ESOURCE: {
    GET_TEMPLATES: "/api/ESource/GetTemplates",
    GET_TEMPLATE_BY_ID: "/api/ESource/GetTemplateById",
    SAVE_TEMPLATE: "/api/ESource/SaveTemplate",
    PUBLISH_TEMPLATE: "/api/ESource/PublishTemplate",
    CREATE_DRAFT_VERSION: "/api/ESource/CreateDraftVersion",
    GET_INSTANCES: "/api/ESource/GetInstances",
    CREATE_INSTANCE: "/api/ESource/CreateInstance",
    GET_INSTANCE_BY_ID: "/api/ESource/GetInstanceById",
    SAVE_DRAFT: "/api/ESource/SaveDraftResponse",
    SUBMIT_REVIEW: "/api/ESource/SubmitForReview",
    SIGN: "/api/ESource/SignInstance",
    LOCK: "/api/ESource/LockInstance",
    AMEND: "/api/ESource/AmendField",
    AUDIT_LOG: "/api/ESource/GetAuditLog",
    CREATE_FILL_LINK: "/api/ESource/CreateFillLink",
    REVOKE_FILL_LINK: "/api/ESource/RevokeFillLink",
    PUBLIC_GET: "/api/ESourcePublic/GetForm",
    PUBLIC_SAVE: "/api/ESourcePublic/SaveDraft",
    PUBLIC_SUBMIT: "/api/ESourcePublic/Submit",
  },

  EPSF: {
    GET_CUSTODY_SUMMARY: "/api/Epsf/GetCustodySummary",
    LIST_PRODUCTS: "/api/Epsf/ListProducts",
    LIST_STUDY_SITES: "/api/Epsf/ListStudySites",
    SAVE_PRODUCT: "/api/Epsf/SaveProduct",
    GET_STUDY_CONFIG: "/api/Epsf/GetStudyConfig",
    SAVE_STUDY_CONFIG: "/api/Epsf/SaveStudyConfig",
    LIST_TXNS: "/api/Epsf/ListTxns",
    LIST_BALANCES: "/api/Epsf/ListBalances",
    LIST_KITS: "/api/Epsf/ListKits",
    GET_RECEIVE_MODES: "/api/Epsf/GetReceiveModes",
    VALIDATE_KIT_SHIPMENT: "/api/Epsf/ValidateKitShipment",
    CREATE_RECEIVE_SCAN_SESSION: "/api/Epsf/CreateReceiveScanSession",
    POLL_RECEIVE_SCAN_EVENTS: "/api/Epsf/PollReceiveScanEvents",
    CLOSE_RECEIVE_SCAN_SESSION: "/api/Epsf/CloseReceiveScanSession",
    PUBLIC_GET_RECEIVE_SCAN_SESSION: "/api/EpsfPublic/GetReceiveScanSession",
    PUBLIC_POST_RECEIVE_SCAN: "/api/EpsfPublic/PostReceiveScan",
    PUBLIC_CLOSE_RECEIVE_SCAN_SESSION: "/api/EpsfPublic/CloseReceiveScanSession",
    LIST_STUDY_IMP_PRODUCTS: "/api/Epsf/ListStudyImpProducts",
    SAVE_STUDY_IMP_PRODUCT: "/api/Epsf/SaveStudyImpProduct",
    LIST_STUDY_IMP_KIT_MAPS: "/api/Epsf/ListStudyImpKitMaps",
    LIST_RECEIVABLE_BATCHES: "/api/Epsf/ListReceivableBatches",
    SAVE_STUDY_IMP_KIT_MAPS: "/api/Epsf/SaveStudyImpKitMaps",
    POST_EMERGENCY_UNBLIND: "/api/Epsf/PostEmergencyUnblind",
    LIST_STUDY_PARTICIPANTS: "/api/Epsf/ListStudyParticipants",
    LIST_PARTICIPANT_IMP_SUMMARY: "/api/Epsf/ListParticipantImpSummary",
    LIST_PARTICIPANT_IMP_DISPENSES: "/api/Epsf/ListParticipantImpDispenses",
    LIST_RECONCILES: "/api/Epsf/ListReconciles",
    POST_RECONCILE: "/api/Epsf/PostReconcile",
    POST_RECEIPT: "/api/Epsf/PostReceipt",
    RELEASE_RECEIPT: "/api/Epsf/ReleaseReceipt",
    CANCEL_RETURN_PENDING: "/api/Epsf/CancelReturnPending",
    LIST_RETURN_WEEKLY_TREND: "/api/Epsf/ListReturnWeeklyTrend",
    LIST_HELD_RECEIPTS: "/api/Epsf/ListHeldReceipts",
    GET_RECEIPT_DETAIL: "/api/Epsf/GetReceiptDetail",
    UPLOAD_RECEIPT_EVIDENCE: "/api/Epsf/UploadReceiptEvidence",
    CREATE_TRANSFER: "/api/Epsf/CreateTransfer",
    DESPATCH_TRANSFER: "/api/Epsf/DespatchTransfer",
    CONFIRM_TRANSFER_ARRIVAL: "/api/Epsf/ConfirmTransferArrival",
    LIST_TRANSFERS: "/api/Epsf/ListTransfers",
    POST_DISPENSE: "/api/Epsf/PostDispense",
    CREATE_DISPENSE_REQUEST: "/api/Epsf/CreateDispenseRequest",
    CONFIRM_DISPENSE_REQUEST: "/api/Epsf/ConfirmDispenseRequest",
    CREATE_DESTROY_REQUEST: "/api/Epsf/CreateDestroyRequest",
    CONFIRM_DESTROY_REQUEST: "/api/Epsf/ConfirmDestroyRequest",
    CREATE_RETURN_REQUEST: "/api/Epsf/CreateReturnRequest",
    CONFIRM_RETURN_REQUEST: "/api/Epsf/ConfirmReturnRequest",
    CREATE_STOCK_RETURN_REQUEST: "/api/Epsf/CreateStockReturnRequest",
    CONFIRM_STOCK_RETURN_REQUEST: "/api/Epsf/ConfirmStockReturnRequest",
    CREATE_RECONCILE_REQUEST: "/api/Epsf/CreateReconcileRequest",
    CONFIRM_RECONCILE_REQUEST: "/api/Epsf/ConfirmReconcileRequest",
    POST_RETURN: "/api/Epsf/PostReturn",
    POST_STOCK_RETURN: "/api/Epsf/PostStockReturn",
    LIST_RETURNABLE_DISPENSES: "/api/Epsf/ListReturnableDispenses",
    POST_DESTROY: "/api/Epsf/PostDestroy",
    POST_ADJUSTMENT: "/api/Epsf/PostAdjustment",
    POST_RECALL: "/api/Epsf/PostRecall",
    GET_INSPECTION_PACK: "/api/Epsf/GetInspectionPack",
    LIST_STORAGE_UNITS: "/api/Epsf/ListStorageUnits",
    SAVE_STORAGE_UNIT: "/api/Epsf/SaveStorageUnit",
    LIST_TEMP_READINGS: "/api/Epsf/ListTempReadings",
    LIST_TEMP_EXCURSIONS: "/api/Epsf/ListTempExcursions",
    POST_TEMP_READING: "/api/Epsf/PostTempReading",
    LIST_FORM_TRIGGERS: "/api/Epsf/ListFormTriggers",
    SAVE_FORM_TRIGGER: "/api/Epsf/SaveFormTrigger",
    LIST_FORM_OCCURRENCES: "/api/Epsf/ListFormOccurrences",
    ENSURE_FORM_OCCURRENCE: "/api/Epsf/EnsureFormOccurrence",
    CREATE_MANUAL_FORM_OCCURRENCE: "/api/Epsf/CreateManualFormOccurrence",
    ENSURE_RECURRING_FORM_DUES: "/api/Epsf/EnsureRecurringFormDues",
    LINK_FORM_INSTANCE: "/api/Epsf/LinkFormInstance",
    COMPLETE_FORM_OCCURRENCE: "/api/Epsf/CompleteFormOccurrence",
    LIST_FORM_WORK_ITEMS: "/api/Epsf/ListFormWorkItems",
    SUBMIT_FORM_WORK_ITEM: "/api/Epsf/SubmitFormWorkItem",
    APPROVE_FORM_WORK_ITEM: "/api/Epsf/ApproveFormWorkItem",
    REJECT_FORM_WORK_ITEM: "/api/Epsf/RejectFormWorkItem",
    GET_FORM_LIFECYCLE: "/api/Epsf/GetFormLifecycle",
    GET_FORM_KIT_CONTEXT: "/api/Epsf/GetFormKitContext",
    CHECK_FORM_RELEASE_BLOCKERS: "/api/Epsf/CheckFormReleaseBlockers",
    CHECK_FORM_ACTION_BLOCKERS: "/api/Epsf/CheckFormActionBlockers",
    LIST_DUTIES: "/api/Epsf/ListDuties",
    LIST_DUTY_ASSIGNMENTS: "/api/Epsf/ListDutyAssignments",
    ASSIGN_DUTY: "/api/Epsf/AssignDuty",
    REVOKE_DUTY_ASSIGNMENT: "/api/Epsf/RevokeDutyAssignment",
    CHECK_DUTY: "/api/Epsf/CheckDuty",
    LIST_BINDER_SECTIONS: "/api/Epsf/ListBinderSections",
    LIST_BINDER_DOCUMENTS: "/api/Epsf/ListBinderDocuments",
    GET_BINDER_DOCUMENT: "/api/Epsf/GetBinderDocument",
    ARCHIVE_BINDER_DOCUMENT: "/api/Epsf/ArchiveBinderDocument",
    UPLOAD_BINDER_DOCUMENT: "/api/Epsf/UploadBinderDocument",
    SAVE_BINDER_TRIGGER_FILING: "/api/Epsf/SaveBinderTriggerFiling",
    FILE_BINDER_FORM_EVIDENCE: "/api/Epsf/FileBinderFormEvidence",
    LIST_BINDER_UPLOAD_REQUESTS: "/api/Epsf/ListBinderUploadRequests",
    SAVE_BINDER_CUSTOM_SECTION: "/api/Epsf/SaveBinderCustomSection",
    MOVE_BINDER_SECTION: "/api/Epsf/MoveBinderSection",
    MOVE_BINDER_DOCUMENT: "/api/Epsf/MoveBinderDocument",
    SAVE_BINDER_UPLOAD_REQUEST: "/api/Epsf/SaveBinderUploadRequest",
    CANCEL_BINDER_UPLOAD_REQUEST: "/api/Epsf/CancelBinderUploadRequest",
  },

  // Analytics endpoints
  ANALYTICS: {
    DASHBOARD: "/api/Analytics/Dashboard",
    REPORTS: "/api/Analytics/Reports",
    EXPORT: "/api/Analytics/Export",
  },

  // Timeline / Page visit analytics (logged user + organization). Enable by setting real backend URLs.
  TIMELINE_ANALYTICS: {
    GET: "/api/TimelineAnalytics/Get",   // GET: userId, organizationId → { recentVisits, pageStats }
    SAVE: "/api/TimelineAnalytics/Save", // POST: { userId, organizationId, recentVisits, pageStats }
  },
};

export default API_ENDPOINTS;
